import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, InputNumber, Radio } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useCallback, useState } from 'react';

import { DurationDescription } from 'app/components/commons/Experience/Types';
import { EmojiModal } from 'app/components/commons/Package/Headlines/EmojiModal/EmojiModal';
import { parseOpeningHoursFromAPI } from 'app/components/commons/TimeRangeSection/utils';
import { Option, Select } from 'app/components/fields/Select';
import { TimePicker } from 'app/components/fields/dayjsPickers';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { useGetBrands } from 'app/hooks/data/useBrands';
import { useCountries } from 'app/hooks/data/useCountries';
import type { Picture } from 'app/typings';
import type { Experience, ExperienceCategory } from 'app/typings/experiences';
import { dayjs } from 'app/utils/dayjs';
import { useDebounce } from 'app/utils/hooks/useDebounce';

import { Mode } from '../../Hotel/Types';
import { PictureComponent } from '../../PictureComponent/PictureComponent';
import { TooltipLabel } from '../../TooltipLabel/TooltipLabel';
import type { ExperienceFormValues } from '../ExperienceForm';

import { BrandSelect } from './BrandSelect/BrandSelect';
import { Cascada } from './Cascada/Cascada';
import { HotelSelect } from './HotelSelect/HotelSelect';

const RULES = [{ required: true, message: 'Required' }];

const RESULT_LIMIT = 50;

export const BasicInfo = ({
  isBuilder,
  disabled,
  mode = Mode.Edit,
  values,
  setFieldsValue,
  experienceCategories,
  category,
  experienceType,
  setExperienceType,
  isOriginal,
}: Props) => {
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isHover, setIsOver] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const { data: countries } = useCountries();
  const { data: hotels } = useHotels();

  const debouncedSearch = useDebounce(brandSearch);

  const makePayload = useCallback(
    () => ({
      limit: RESULT_LIMIT,
      search: debouncedSearch ?? '',
    }),
    [debouncedSearch]
  );

  const { data: brands } = useGetBrands(makePayload());

  const displayDurationDescription = (
    description: DurationDescription | string | null
  ) => {
    if (description === null) {
      return '';
    }

    return description;
  };

  const durationDescriptionOption = (
    Object.keys(DurationDescription) as (keyof typeof DurationDescription)[]
  ).map((key) => (
    <Option key={key} value={DurationDescription[key]}>
      {DurationDescription[key]}
    </Option>
  ));

  const onEmojiSelect = useCallback(
    (emoji: Picture) => {
      if (!values) {
        return;
      }

      setFieldsValue({
        ...values,
        emoji,
      });
      setIsEmojiOpen(false);
    },
    [values, setFieldsValue]
  );

  const handleExperienceType = (event: RadioChangeEvent) =>
    setExperienceType(event?.target?.value);

  return (
    <>
      {!isBuilder && (
        <div className="input-row">
          <Form.Item label="Experience type">
            <Radio.Group
              value={experienceType}
              onChange={handleExperienceType}
              disabled={true}
            >
              <Radio value={1}>Hotel</Radio>
              <Radio value={2}>Staycation</Radio>
            </Radio.Group>
          </Form.Item>
        </div>
      )}
      {!isBuilder && !isOriginal && values ? (
        <HotelSelect
          hotels={hotels ?? []}
          hotelId={values.hotelId}
          mode={mode}
          disabled={true}
          onChange={(newValue) => {
            setFieldsValue({
              ...values,
              hotelId: newValue,
              spaceIds: [],
              accessMode: null,
              openingHours: parseOpeningHoursFromAPI(
                undefined
              ) as unknown as Experience['openingHours'],
              roomServiceAvailable: false,
              roomServiceIncluded: false,
              roomServicePrice: null,
              pictures: [],
              price: 0,
            });
          }}
        />
      ) : null}
      {!isBuilder && isOriginal ? (
        <div className="input-row">
          <Form.Item label="Country" name="countryId" rules={RULES}>
            <Select
              placeholder="Country"
              disabled={disabled || mode === Mode.View}
            >
              {countries?.map((country) => (
                <Option key={country.id} value={country.id}>
                  {country.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      ) : null}
      {!isBuilder && (
        <div className="input-row">
          <Form.Item label="Category" name="categoryId" rules={RULES}>
            <Cascada
              dataSource={experienceCategories}
              onChange={(newValue) => {
                setFieldsValue({
                  ...values,
                  categoryId: newValue,
                  brand: null,
                  duration: null,
                  durationDescription: null,
                  time: null,
                  quantity: null,
                  quantityDescription: null,
                  locations: null,
                  description: null,
                  emoji: null,
                  menu: null,
                  treatmentList: null,
                });
              }}
              mode={mode}
            />
          </Form.Item>
        </div>
      )}
      {!!category && (
        <>
          {!isBuilder && (
            <div className="input-row">
              <Form.Item
                label="Emoji"
                name="emoji"
                rules={RULES}
                valuePropName="key"
              >
                <div className="experience__avatar-container">
                  <div
                    onClick={
                      mode === 'edit' ? () => setIsEmojiOpen(true) : undefined
                    }
                    className={`experience__avatar ${
                      mode === 'edit' ? 'experience__avatar-clickable' : ''
                    }`}
                    key={values?.emoji?.pictureId}
                  >
                    {values?.emoji && (
                      <PictureComponent {...values.emoji} width={40} />
                    )}
                  </div>
                  {mode === 'edit' && (
                    <div
                      onMouseLeave={() => setIsOver(false)}
                      onMouseOver={() => setIsOver(true)}
                      className="experience__avatar__hover-area"
                    >
                      {!!isHover && (
                        <div className="upload-item__hover-buttons">
                          <EditOutlined onClick={() => setIsEmojiOpen(true)} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Item>
              <EmojiModal
                visible={isEmojiOpen}
                onSelect={onEmojiSelect}
                onCancel={() => setIsEmojiOpen(false)}
              />
            </div>
          )}
          {!!category.brand && (mode !== Mode.View || !!values.brand) && (
            <div className="input-row">
              <Form.Item label="Brand" name="brand">
                {mode !== Mode.View ? (
                  <BrandSelect
                    experiencesBrands={brands ?? []}
                    setSearch={setBrandSearch}
                  />
                ) : (
                  <div>{values.brand || ''}</div>
                )}
              </Form.Item>
              {mode !== Mode.View && (
                <div className="description">
                  <InfoCircleOutlined />
                  <div>
                    If applicable, it will be highlighted in the activity title
                  </div>
                </div>
              )}
            </div>
          )}
          {!!category.duration &&
            (mode !== Mode.View ||
              (!!values.duration && !!values.durationDescription)) && (
              <div className="input-row">
                <Form.Item label="Duration">
                  <div className="FIELD">
                    {mode !== Mode.View ? (
                      <div className="not-price-kind">
                        <Form.Item noStyle name="duration">
                          <InputNumber
                            value={values.duration}
                            min={1}
                            onChange={(duration) => {
                              if (duration === null) {
                                setFieldsValue({
                                  ...values,
                                  duration: null,
                                  durationDescription: null,
                                });
                              }
                            }}
                          />
                        </Form.Item>
                        <Form.Item noStyle name="durationDescription">
                          <Select
                            showSearch
                            relativePopupContainer={(node) =>
                              (node.parentNode as HTMLElement)
                                .parentNode as HTMLElement
                            }
                            value={
                              values.durationDescription === null
                                ? 'empty'
                                : values.durationDescription
                            }
                            onSelect={(durationDescription) => {
                              if (durationDescription === 'empty') {
                                setFieldsValue({
                                  ...values,
                                  duration: null,
                                  durationDescription: null,
                                });
                              }
                            }}
                          >
                            <Option key={'empty'} value={'empty'}>
                              {' '}
                            </Option>
                            {durationDescriptionOption}
                          </Select>
                        </Form.Item>
                      </div>
                    ) : (
                      <div>
                        {`${values.duration || 0} ${displayDurationDescription(
                          values.durationDescription
                        )}`}
                      </div>
                    )}
                  </div>
                </Form.Item>
                {mode !== Mode.View && (
                  <div className="description">
                    <InfoCircleOutlined />
                    <div>
                      If applicable, it will be highlighted in the activity
                      title
                    </div>
                  </div>
                )}
              </div>
            )}
          {!!category.time && (mode !== Mode.View || !!values.time) && (
            <div className="input-row">
              <Form.Item label="Time" name="time">
                {mode !== Mode.View ? (
                  <TimePicker format="HH:mm" />
                ) : (
                  <div>
                    {dayjs.isDayjs(values.time)
                      ? values.time.format('HH:mm')
                      : values.time || ''}
                  </div>
                )}
              </Form.Item>
              {mode !== Mode.View && (
                <div className="description">
                  <InfoCircleOutlined />
                  <div>
                    If applicable, it will be highlighted in the activity title
                  </div>
                </div>
              )}
            </div>
          )}
          {!!category.quantityTitle &&
            (mode !== Mode.View ||
              (!!values.quantity && !!values.quantityDescription)) && (
              <div className="input-row">
                <Form.Item label="Quantity">
                  <div className="FIELD">
                    {mode !== Mode.View ? (
                      <div className="not-price-kind">
                        <Form.Item noStyle name="quantity">
                          <InputNumber min={1} />
                        </Form.Item>
                        {!!category.quantityDescription && (
                          <Form.Item noStyle name="quantityDescription">
                            <Select
                              showSearch
                              relativePopupContainer={(node) =>
                                (node.parentNode as HTMLElement)
                                  .parentNode as HTMLElement
                              }
                            >
                              <Option key="empty" value="empty">
                                {' '}
                              </Option>
                              <Option key="per pers" value="per pers">
                                per pers
                              </Option>
                              <Option key="per stay" value="per stay">
                                per stay
                              </Option>
                            </Select>
                          </Form.Item>
                        )}
                      </div>
                    ) : (
                      <div>
                        {`${values.quantity || 0} ${
                          values.quantityDescription
                        }`}
                      </div>
                    )}
                  </div>
                </Form.Item>
                {mode !== Mode.View && (
                  <div className="description">
                    <InfoCircleOutlined />
                    <div>
                      If applicable, it will be highlighted in the activity
                      title
                    </div>
                  </div>
                )}
              </div>
            )}
          {!!category.locations &&
            (mode !== Mode.View || !!values.locations) && (
              <div className="input-row">
                <Form.Item label="Location" name="locations">
                  {mode !== Mode.View ? (
                    <Input />
                  ) : (
                    <div>{values.locations || ''}</div>
                  )}
                </Form.Item>
                {mode !== Mode.View && (
                  <div className="description">
                    <InfoCircleOutlined />
                    <div>
                      If applicable, it will be highlighted in the activity
                      title
                    </div>
                  </div>
                )}
              </div>
            )}
          {!isBuilder && (mode !== Mode.View || !!values.tagline) && (
            <div className="input-row">
              <Form.Item label="Tagline" name="tagline">
                {mode !== Mode.View ? (
                  <Input.TextArea rows={2} />
                ) : (
                  <div className="view-text__area">{values.tagline || ''}</div>
                )}
              </Form.Item>
              {mode !== Mode.View && (
                <div className="description">
                  <InfoCircleOutlined />
                  <div>
                    <p>
                      The tagline will appear as a preview of the description.
                    </p>
                    <p>
                      It should be concise and explain the activity in a few
                      words
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          {(mode !== Mode.View || !!values.description) && (
            <div className="input-row">
              <Form.Item
                label={<TooltipLabel label="Description" />}
                name="description"
              >
                {mode !== Mode.View ? (
                  <Input.TextArea rows={4} />
                ) : (
                  <div className="view-text__area">
                    {values.description || ''}
                  </div>
                )}
              </Form.Item>
            </div>
          )}
          {!isBuilder &&
            category.menu &&
            (mode !== Mode.View || !!values.menu) && (
              <div className="input-row">
                <Form.Item label={<TooltipLabel label="Menu" />} name="menu">
                  {mode !== Mode.View ? (
                    <Input.TextArea rows={4} />
                  ) : (
                    <div className="view-text__area">{values.menu || ''}</div>
                  )}
                </Form.Item>
              </div>
            )}
          {!isBuilder &&
            category.treatmentList &&
            (mode !== Mode.View || !!values.treatmentList) && (
              <div className="input-row">
                <Form.Item
                  label={<TooltipLabel label="Treatment list" />}
                  name="treatmentList"
                >
                  {mode !== Mode.View ? (
                    <Input.TextArea rows={4} />
                  ) : (
                    <div className="view-text__area">
                      {values.treatmentList || ''}
                    </div>
                  )}
                </Form.Item>
              </div>
            )}
          {!isBuilder &&
            (mode !== Mode.View || !!values.importantInformations) && (
              <div className="input-row">
                <Form.Item
                  label={<TooltipLabel label="Important information" />}
                  name="importantInformations"
                >
                  {mode !== Mode.View ? (
                    <Input.TextArea rows={4} />
                  ) : (
                    <div className="view-text__area">
                      {values.importantInformations || ''}
                    </div>
                  )}
                </Form.Item>
              </div>
            )}
        </>
      )}
    </>
  );
};

type Props = {
  isBuilder?: boolean;
  disabled: boolean;
  mode?: string;
  values: ExperienceFormValues;
  setFieldsValue: (newValues: ExperienceFormValues) => void;
  experienceCategories: ExperienceCategory[];
  category?: ExperienceCategory;
  experienceType: number;
  setExperienceType: (newType: number) => void;
  isOriginal: boolean;
};
